import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { LogOut, Calendar, Clock, CheckSquare, MessageSquare, MapPin, Package, TrendingUp, User, Plus, FileText, Upload, HelpCircle, Play, Square, GripVertical, Building2, HardHat, RefreshCw, Gavel, Building, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import MaterialOrderForm from '@/components/MaterialOrderForm';
import { ProjectCombobox } from '@/components/dashboard/ProjectCombobox';
import ChangePasswordDialog from '@/components/employee/ChangePasswordDialog';
import { MyTasksCard } from '@/components/MyTasksCard';
import ImageViewerDialog from '@/components/employee/ImageViewerDialog';
import WorkProgressDialog from '@/components/employee/WorkProgressDialog';
import { hasFeature } from '@/utils/features';
import { PushNotificationManager } from '@/components/PushNotificationManager';
import { PWAInstallBanner } from '@/components/PWAInstallBanner';
import { IOSInstallBanner } from '@/components/IOSInstallBanner';
import { PersonalDetailsDialog } from '@/components/PersonalDetailsDialog';
import { UnresolvedEntriesAlert } from '@/components/dashboard/UnresolvedEntriesAlert';
import { OnboardingTour } from '@/components/OnboardingTour';
import { HelpCenter } from '@/components/HelpCenter';
import { forceAppUpdate } from '@/lib/appUpdate';

// Shared Styles
import '@/styles/companyHeader.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Note colors for bulletin board
const NOTE_COLORS = [
  { name: 'Yellow', value: 'yellow', bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-800' },
  { name: 'Blue', value: 'blue', bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-800' },
  { name: 'Green', value: 'green', bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-800' },
  { name: 'Pink', value: 'pink', bg: 'bg-pink-100', border: 'border-pink-300', text: 'text-pink-800' },
  { name: 'Purple', value: 'purple', bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-800' },
  { name: 'Orange', value: 'orange', bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-800' },
];

export default function ProjectTeamsDashboard({ user, setUser }) {
  const navigate = useNavigate();
  
  // Standard dashboard state (same as FieldDashboard)
  const [schedules, setSchedules] = useState([]);
  const [todaySchedule, setTodaySchedule] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [messages, setMessages] = useState([]);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [clockInTime, setClockInTime] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [hoursToday, setHoursToday] = useState(0);
  const [activeTimeEntry, setActiveTimeEntry] = useState(null);
  const [messageText, setMessageText] = useState('');
  
  // Personal Stats State
  const [personalStats, setPersonalStats] = useState({
    userWeeklyHours: 0,
    userBiWeeklyHours: 0,
    userBiWeeklyPay: 0,
    biWeeklyPeriodStart: null,
    biWeeklyPeriodEnd: null
  });
  const [showPersonalStatsDialog, setShowPersonalStatsDialog] = useState(false);
  const [personalStatsType, setPersonalStatsType] = useState('');
  const [personalStatsData, setPersonalStatsData] = useState(null);
  const [personalStatsLoading, setPersonalStatsLoading] = useState(false);
  const [materialOrders, setMaterialOrders] = useState([]);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Location monitoring state
  const [showLocationChangeDialog, setShowLocationChangeDialog] = useState(false);
  const [locationChangeData, setLocationChangeData] = useState(null);
  const [updatingWorkType, setUpdatingWorkType] = useState(false);
  
  // Projects - separated by stage
  const [preConstructionProjects, setPreConstructionProjects] = useState([]);
  const [constructionProjects, setConstructionProjects] = useState([]);
  const [biddingProjects, setBiddingProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [activeProjectTab, setActiveProjectTab] = useState('construction');
  
  // Help Center state
  const [showHelpCenter, setShowHelpCenter] = useState(false);
  
  // Material Order & Transfer
  const [showMaterialOrderDialog, setShowMaterialOrderDialog] = useState(false);
  const [showOrdersDialog, setShowOrdersDialog] = useState(false);
  const [showProjectsDialog, setShowProjectsDialog] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  
  // Expense state
  const [expenses, setExpenses] = useState([]);
  const [expenseForm, setExpenseForm] = useState({
    expense_type: 'parking',
    amount: '',
    description: '',
    pay_period_start: '',
    pay_period_end: ''
  });
  const [expenseReceipts, setExpenseReceipts] = useState([]);
  
  // Transfer state
  const [returns, setReturns] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [returnForm, setReturnForm] = useState({
    project_id: '',
    reason: '',
    materials_description: ''
  });
  const [transferForm, setTransferForm] = useState({
    source_project_id: '',
    destination_project_id: '',
    reason: '',
    materials_description: ''
  });
  
  // Upload states
  const [uploadingReceipts, setUploadingReceipts] = useState(false);
  
  // Projects assigned to this user (for dialogs)
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  
  // Profile & Settings
  const [showMyProfileDialog, setShowMyProfileDialog] = useState(false);
  const [showPersonalDetailsDialog, setShowPersonalDetailsDialog] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [myProfile, setMyProfile] = useState(null);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [companySettings, setCompanySettings] = useState({ 
    name: '', 
    tagline: '',
    logo_url: '',
    name_color: '#1e293b',
    name_font: 'Inter'
  });
  
  // Certificates, Licenses, Credentials state
  const [showAddCertificateDialog, setShowAddCertificateDialog] = useState(false);
  const [showAddLicenseDialog, setShowAddLicenseDialog] = useState(false);
  const [showAddCredentialDialog, setShowAddCredentialDialog] = useState(false);
  const [newCertificate, setNewCertificate] = useState({ name: '', issuing_authority: '', issue_date: '', expiry_date: '', certificate_number: '' });
  const [newLicense, setNewLicense] = useState({ license_type: '', license_number: '', state: '', issue_date: '', expiry_date: '' });
  const [newCredential, setNewCredential] = useState({ credential_type: '', credential_number: '', expiry_date: '', notes: '' });
  
  // ===== PROJECT TIME TRACKING (Office Staff Feature) =====
  const [activeProjectTime, setActiveProjectTime] = useState(null);
  const [projectTimeEntries, setProjectTimeEntries] = useState([]);
  const [projectTimeSummary, setProjectTimeSummary] = useState([]);
  const [selectedProjectForTime, setSelectedProjectForTime] = useState('');
  
  // ===== BULLETIN BOARD (Office Staff Feature) =====
  const [bulletinNotes, setBulletinNotes] = useState([]);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteForm, setNoteForm] = useState({
    title: '',
    content: '',
    color: 'yellow',
    is_shared: false,
    pinned: false,
    priority: 0
  });

  // Elapsed time for running clock display
  const [elapsedTime, setElapsedTime] = useState('00:00:00');
  
  // Helper function to format dates without timezone shift
  const formatDateDisplay = (dateString, options = {}) => {
    if (!dateString) return '';
    // Parse the date string directly without timezone conversion
    const parts = dateString.split('T')[0].split('-');
    if (parts.length !== 3) return dateString;
    const [year, month, day] = parts;
    
    // If options include weekday, format as "Wednesday, Apr 8, 2026"
    if (options.weekday) {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      // Create date at noon UTC to avoid timezone shift
      const date = new Date(`${year}-${month}-${day}T12:00:00Z`);
      const weekday = weekdays[date.getUTCDay()];
      const monthName = months[parseInt(month) - 1];
      return `${weekday}, ${monthName} ${parseInt(day)}, ${year}`;
    }
    
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };
  
  // Update elapsed time every second when clocked in
  useEffect(() => {
    let interval;
    if (isClockedIn && clockInTime) {
      const updateElapsed = () => {
        const now = new Date();
        const clockIn = new Date(clockInTime);
        const diff = Math.floor((now - clockIn) / 1000);
        
        const hours = Math.floor(diff / 3600);
        const minutes = Math.floor((diff % 3600) / 60);
        const seconds = diff % 60;
        
        setElapsedTime(
          `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        );
      };
      
      updateElapsed();
      interval = setInterval(updateElapsed, 1000);
    } else {
      setElapsedTime('00:00:00');
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isClockedIn, clockInTime]);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
    fetchExpenses();
    fetchReturns();
    fetchTransfers();
    fetchCompanySettings();
    fetchMyProfile();
    getCurrentLocation();
    
    // Office staff specific
    fetchBulletinNotes();
    fetchProjectTimeData();
    fetchProjectsByStage();
  }, [user?.role]);

  // Location monitoring for office staff - check every 2 minutes when clocked in
  useEffect(() => {
    let locationCheckInterval;
    
    if (isClockedIn) {
      const checkLocation = async () => {
        if (!navigator.geolocation) return;
        
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const token = localStorage.getItem('token');
            try {
              const response = await fetch(`${API_URL}/api/time/check-location`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude
                })
              });
              
              if (response.ok) {
                const data = await response.json();
                if (data.should_prompt) {
                  setLocationChangeData(data);
                  setShowLocationChangeDialog(true);
                }
              }
            } catch (error) {
              console.error('Location check error:', error);
            }
          },
          (error) => {
            console.log('Location monitoring error:', error.message);
          },
          { enableHighAccuracy: true, timeout: 10000 }
        );
      };
      
      // Check immediately and then every 2 minutes
      checkLocation();
      locationCheckInterval = setInterval(checkLocation, 2 * 60 * 1000);
    }
    
    return () => {
      if (locationCheckInterval) clearInterval(locationCheckInterval);
    };
  }, [isClockedIn]);

  // Handler for dismissing location change and marking work type
  const handleDismissLocationChange = async (workType) => {
    if (!locationChangeData?.time_entry_id) return;
    
    const token = localStorage.getItem('token');
    setUpdatingWorkType(true);
    
    try {
      const response = await fetch(`${API_URL}/api/time/update-work-type`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          time_entry_id: locationChangeData.time_entry_id,
          work_type: workType
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowLocationChangeDialog(false);
        setLocationChangeData(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update work type');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setUpdatingWorkType(false);
    }
  };

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    try {
      // Fetch schedules
      const schedulesRes = await fetch(`${API_URL}/api/employee/schedules`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (schedulesRes.ok) {
        const schedulesData = await schedulesRes.json();
        setSchedules(schedulesData);
        // Find today's schedule
        const today = new Date().toISOString().split('T')[0];
        const todayS = schedulesData.find(s => s.date === today);
        setTodaySchedule(todayS);
      }

      // Fetch tasks
      const tasksRes = await fetch(`${API_URL}/api/tasks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (tasksRes.ok) setTasks(await tasksRes.json());

      // Fetch time entries - use correct /api/time/entries endpoint
      const timeRes = await fetch(`${API_URL}/api/time/entries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (timeRes.ok) {
        const entries = await timeRes.json();
        // Filter to only entries marked as own (for non-admin users this is all entries)
        const ownEntries = entries.filter(e => e.is_own_entry === true);
        setTimeEntries(ownEntries);
        
        // Calculate hours today using correct field names (clock_in, clock_out)
        const today = new Date().toISOString().split('T')[0];
        const todayEntries = ownEntries.filter(e => e.clock_in?.startsWith(today));
        let totalMinutes = 0;
        todayEntries.forEach(entry => {
          if (entry.clock_in && entry.clock_out) {
            const diff = new Date(entry.clock_out) - new Date(entry.clock_in);
            totalMinutes += diff / (1000 * 60);
          }
        });
        setHoursToday((totalMinutes / 60).toFixed(2));
        
        // Check if clocked in - use correct field names
        const active = ownEntries.find(e => e.clock_in && !e.clock_out);
        setIsClockedIn(!!active);
        setActiveTimeEntry(active);
        // Set clock in time for running timer
        if (active && active.clock_in) {
          setClockInTime(new Date(active.clock_in));
        } else {
          setClockInTime(null);
        }
      }

      // Fetch team messages
      const messagesRes = await fetch(`${API_URL}/api/team-messages?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (messagesRes.ok) setMessages(await messagesRes.json());

      // Fetch material orders
      const ordersRes = await fetch(`${API_URL}/api/material-orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (ordersRes.ok) setMaterialOrders(await ordersRes.json());

      // Fetch all projects
      const projectsRes = await fetch(`${API_URL}/api/projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (projectsRes.ok) {
        const projectsData = await projectsRes.json();
        const activeProjects = projectsData.filter(p => p.is_active);
        setAllProjects(activeProjects);
        setProjects(activeProjects); // Also set projects for dialogs
      }
      
      // Fetch dashboard stats for personal hours
      const statsRes = await fetch(`${API_URL}/api/dashboard/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (statsRes.ok) {
        const stats = await statsRes.json();
        setPersonalStats({
          userWeeklyHours: stats.user_weekly_hours || 0,
          userBiWeeklyHours: stats.user_bi_weekly_hours || 0,
          userBiWeeklyPay: stats.user_bi_weekly_pay || 0,
          biWeeklyPeriodStart: stats.bi_weekly_period_start,
          biWeeklyPeriodEnd: stats.bi_weekly_period_end
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchProjectsByStage = async () => {
    const token = localStorage.getItem('token');
    try {
      // Fetch pre-construction projects
      const preRes = await fetch(`${API_URL}/api/projects?stage=pre_construction`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (preRes.ok) {
        const data = await preRes.json();
        setPreConstructionProjects(data.filter(p => p.is_active));
      }

      // Fetch construction projects
      const conRes = await fetch(`${API_URL}/api/projects?stage=construction`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (conRes.ok) {
        const data = await conRes.json();
        setConstructionProjects(data.filter(p => p.is_active));
      }

      // Fetch bidding projects
      const bidRes = await fetch(`${API_URL}/api/projects?stage=bidding`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (bidRes.ok) {
        const data = await bidRes.json();
        setBiddingProjects(data.filter(p => p.is_active));
      }
    } catch (error) {
      console.error('Error fetching projects by stage:', error);
      // Fallback: use all projects as construction
      setConstructionProjects(allProjects);
    }
  };

  const fetchExpenses = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/expenses/my-expenses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setExpenses(await res.json());
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  };

  const fetchReturns = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/material-returns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setReturns(await res.json());
    } catch (error) {
      console.error('Error fetching returns:', error);
    }
  };

  const fetchTransfers = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/material-transfers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setTransfers(await res.json());
    } catch (error) {
      console.error('Error fetching transfers:', error);
    }
  };

  const fetchCompanySettings = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/company/branding`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const settings = await res.json();
        
        // Use logo_base64 if available (persistent), otherwise use logo_url
        let logoUrl = settings.logo_base64 || settings.logo_url || '';
        if (logoUrl && !logoUrl.startsWith('data:') && (logoUrl.startsWith('/uploads/') || logoUrl.startsWith('/api/uploads/'))) {
          logoUrl = `${API_URL}${logoUrl}`;
        }
        
        setCompanySettings({
          name: settings.name || '',
          tagline: settings.tagline || '',
          logo_url: logoUrl,
          name_color: settings.name_color || '#1e293b',
          name_font: settings.name_font || 'Inter'
        });
      }
    } catch (error) {
      console.error('Error fetching company branding:', error);
    }
  };

  const fetchMyProfile = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/my-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const profile = await res.json();
        setMyProfile(profile);
        setIsFirstTimeUser(profile.is_first_time_user);
        
        // For office staff - certificates are optional, just show a friendly welcome
        const profilePromptDismissed = user?.id ? localStorage.getItem(`profile_prompt_dismissed_${user.id}`) : null;
        if (profile.is_first_time_user && !profilePromptDismissed && user?.id) {
          setTimeout(() => {
            toast.info('Welcome! Feel free to add any professional certifications to your profile (optional for office staff).');
            // Mark as dismissed so it won't show again
            localStorage.setItem(`profile_prompt_dismissed_${user.id}`, 'true');
          }, 2000);
        }
        
        // Only warn about EXPIRED certificates (not missing ones - they're optional for office staff)
        const expiredCerts = (profile.certificates || []).filter(cert => {
          if (!cert.expiry_date) return false;
          return new Date(cert.expiry_date) < new Date();
        });
        
        if (expiredCerts.length > 0) {
          setTimeout(() => {
            toast.warning(`You have ${expiredCerts.length} expired certificate(s). Consider updating them.`, {
              duration: 5000
            });
          }, 3000);
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  // ===== CERTIFICATE/LICENSE/CREDENTIAL FUNCTIONS =====
  const addMyCertificate = async () => {
    if (!newCertificate.name.trim()) {
      toast.error('Certificate name is required');
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/my-profile/certificates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCertificate)
      });
      if (response.ok) {
        toast.success('Certificate added');
        setNewCertificate({ name: '', issuing_authority: '', issue_date: '', expiry_date: '', certificate_number: '' });
        setShowAddCertificateDialog(false);
        fetchMyProfile();
      } else {
        toast.error('Failed to add certificate');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const deleteMyCertificate = async (certId) => {
    if (!confirm('Remove this certificate?')) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/my-profile/certificates/${certId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Certificate removed');
        fetchMyProfile();
      } else {
        toast.error('Failed to remove certificate');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const uploadCertificateDocument = async (certId, file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/api/my-profile/certificates/${certId}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        toast.success('Document uploaded');
        fetchMyProfile();
      } else {
        toast.error('Failed to upload document');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const addMyLicense = async () => {
    if (!newLicense.license_type.trim() || !newLicense.license_number.trim()) {
      toast.error('License type and number are required');
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/my-profile/licenses`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newLicense)
      });
      if (response.ok) {
        toast.success('License added');
        setNewLicense({ license_type: '', license_number: '', state: '', issue_date: '', expiry_date: '' });
        setShowAddLicenseDialog(false);
        fetchMyProfile();
      } else {
        toast.error('Failed to add license');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const deleteMyLicense = async (licenseId) => {
    if (!confirm('Remove this license?')) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/my-profile/licenses/${licenseId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('License removed');
        fetchMyProfile();
      } else {
        toast.error('Failed to remove license');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const uploadLicenseDocument = async (licenseId, file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/api/my-profile/licenses/${licenseId}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        toast.success('Document uploaded');
        fetchMyProfile();
      } else {
        toast.error('Failed to upload document');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const addMyCredential = async () => {
    if (!newCredential.credential_type.trim()) {
      toast.error('Credential type is required');
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/my-profile/credentials`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCredential)
      });
      if (response.ok) {
        toast.success('Credential added');
        setNewCredential({ credential_type: '', credential_number: '', expiry_date: '', notes: '' });
        setShowAddCredentialDialog(false);
        fetchMyProfile();
      } else {
        toast.error('Failed to add credential');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const deleteMyCredential = async (credentialId) => {
    if (!confirm('Remove this credential?')) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/my-profile/credentials/${credentialId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Credential removed');
        fetchMyProfile();
      } else {
        toast.error('Failed to remove credential');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const uploadCredentialDocument = async (credentialId, file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/api/my-profile/credentials/${credentialId}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        toast.success('Document uploaded');
        fetchMyProfile();
      } else {
        toast.error('Failed to upload document');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  // ===== BULLETIN BOARD FUNCTIONS =====
  const fetchBulletinNotes = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/bulletin-notes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Sort by pinned first, then by priority
        data.sort((a, b) => {
          if (a.pinned && !b.pinned) return -1;
          if (!a.pinned && b.pinned) return 1;
          return (b.priority || 0) - (a.priority || 0);
        });
        setBulletinNotes(data);
      }
    } catch (error) {
      console.error('Error fetching bulletin notes:', error);
    }
  };

  const handleSaveNote = async () => {
    if (!noteForm.title.trim() || !noteForm.content.trim()) {
      toast.error('Title and content are required');
      return;
    }

    const token = localStorage.getItem('token');
    try {
      const url = editingNote 
        ? `${API_URL}/api/bulletin-notes/${editingNote.id}`
        : `${API_URL}/api/bulletin-notes`;
      
      const response = await fetch(url, {
        method: editingNote ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(noteForm)
      });

      if (response.ok) {
        toast.success(editingNote ? 'Note updated!' : 'Note created!');
        setShowNoteDialog(false);
        setEditingNote(null);
        setNoteForm({ title: '', content: '', color: 'yellow', is_shared: false, pinned: false, priority: 0 });
        fetchBulletinNotes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save note');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/bulletin-notes/${noteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Note deleted');
        fetchBulletinNotes();
      } else {
        toast.error('Failed to delete note');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const moveNoteUp = (index) => {
    if (index === 0) return;
    const newNotes = [...bulletinNotes];
    [newNotes[index], newNotes[index - 1]] = [newNotes[index - 1], newNotes[index]];
    setBulletinNotes(newNotes);
    // Update priorities on backend
    updateNotePriorities(newNotes);
  };

  const moveNoteDown = (index) => {
    if (index === bulletinNotes.length - 1) return;
    const newNotes = [...bulletinNotes];
    [newNotes[index], newNotes[index + 1]] = [newNotes[index + 1], newNotes[index]];
    setBulletinNotes(newNotes);
    updateNotePriorities(newNotes);
  };

  const updateNotePriorities = async (notes) => {
    const token = localStorage.getItem('token');
    try {
      for (let i = 0; i < notes.length; i++) {
        if (notes[i].user_id === user.id) {
          await fetch(`${API_URL}/api/bulletin-notes/${notes[i].id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ ...notes[i], priority: notes.length - i })
          });
        }
      }
    } catch (error) {
      console.error('Error updating priorities:', error);
    }
  };

  const getColorClasses = (colorValue) => {
    return NOTE_COLORS.find(c => c.value === colorValue) || NOTE_COLORS[0];
  };

  // ===== PROJECT TIME TRACKING FUNCTIONS =====
  const fetchProjectTimeData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [entriesRes, summaryRes, activeRes] = await Promise.all([
        fetch(`${API_URL}/api/project-time-entries`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/project-time-summary`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/project-time-entries/active`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (entriesRes.ok) setProjectTimeEntries(await entriesRes.json());
      if (summaryRes.ok) setProjectTimeSummary(await summaryRes.json());
      if (activeRes.ok) {
        const activeData = await activeRes.json();
        setActiveProjectTime(activeData);
      }
    } catch (error) {
      console.error('Error fetching project time data:', error);
    }
  };

  const handleStartProjectTime = async () => {
    if (!selectedProjectForTime) {
      toast.error('Please select a project first');
      return;
    }

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/project-time-entries/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ project_id: selectedProjectForTime })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Started tracking time on ${data.project_name}`);
        setActiveProjectTime(data);
        fetchProjectTimeData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to start project time');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleStopProjectTime = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/project-time-entries/stop`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Stopped tracking. ${data.duration_hours} hours logged on ${data.project_name}`);
        setActiveProjectTime(null);
        fetchProjectTimeData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to stop project time');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  // ===== CLOCK IN/OUT =====
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported');
      return;
    }
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          address: `${position.coords.latitude.toFixed(4)}, ${position.coords.longitude.toFixed(4)}`
        });
        setGettingLocation(false);
      },
      (error) => {
        console.error('Location error:', error);
        setCurrentLocation({
          latitude: 0,
          longitude: 0,
          lat: 0,
          lng: 0,
          address: 'Location unavailable (enable browser permissions)'
        });
        setGettingLocation(false);
      }
    );
  };

  // Handle personal stat card click to show breakdown dialog
  const handlePersonalStatClick = async (type) => {
    const token = localStorage.getItem('token');
    setPersonalStatsLoading(true);
    setShowPersonalStatsDialog(true);
    setPersonalStatsType(type);

    const isBiweekly = type === 'biweekly-hours' || type === 'biweekly-payroll';
    const qs = isBiweekly ? '?weeks=2' : '';
    try {
      const response = await fetch(`${API_URL}/api/time/personal-weekly-breakdown${qs}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPersonalStatsData(data);
      } else {
        toast.error('Failed to fetch personal hours breakdown');
        setShowPersonalStatsDialog(false);
      }
    } catch (error) {
      console.error('Error fetching personal stats:', error);
      toast.error('Connection error');
      setShowPersonalStatsDialog(false);
    } finally {
      setPersonalStatsLoading(false);
    }
  };

  const handleClockIn = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/time/clock-in`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          project_id: null, // Office work - no project
          latitude: currentLocation?.lat || null,
          longitude: currentLocation?.lng || null,
          address: 'Office'
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Clocked in successfully');
        setIsClockedIn(true);
        setClockInTime(new Date(data.clock_in || new Date()));
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to clock in');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleClockOut = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/time/clock-out`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          latitude: currentLocation?.lat || null,
          longitude: currentLocation?.lng || null,
          address: 'Office',
          work_description: 'Office work'
        })
      });

      if (response.ok) {
        toast.success('Clocked out successfully');
        setIsClockedIn(false);
        setClockInTime(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to clock out');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  // Send team message
  const handleSendMessage = async () => {
    if (!messageText.trim()) return;
    
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/team-messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: messageText })
      });

      if (response.ok) {
        setMessageText('');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/auth');
  };

  // Handle expense submission
  const handleSubmitExpense = async (e) => {
    e.preventDefault();
    setUploadingReceipts(true);
    const token = localStorage.getItem('token');
    
    try {
      const formData = new FormData();
      formData.append('expense_type', expenseForm.expense_type);
      formData.append('amount', expenseForm.amount);
      formData.append('description', expenseForm.description);
      
      // Add receipts if any
      expenseReceipts.forEach(file => {
        formData.append('receipts', file);
      });
      
      const response = await fetch(`${API_URL}/api/expenses`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        toast.success('Expense submitted successfully');
        setShowExpenseDialog(false);
        setExpenseForm({ expense_type: 'parking', amount: '', description: '', pay_period_start: '', pay_period_end: '' });
        setExpenseReceipts([]);
        fetchExpenses();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit expense');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setUploadingReceipts(false);
    }
  };

  // Handle return submission
  const handleSubmitReturn = async () => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API_URL}/api/material-returns`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          project_id: returnForm.project_id,
          reason: returnForm.reason,
          materials_description: returnForm.materials_description
        })
      });
      
      if (response.ok) {
        toast.success('Return request submitted successfully');
        setShowReturnDialog(false);
        setReturnForm({ project_id: '', reason: '', materials_description: '' });
        fetchReturns();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit return request');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  // Handle transfer submission
  const handleSubmitTransfer = async () => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API_URL}/api/material-transfers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          source_project_id: transferForm.source_project_id,
          destination_project_id: transferForm.destination_project_id,
          reason: transferForm.reason,
          materials_description: transferForm.materials_description
        })
      });
      
      if (response.ok) {
        toast.success('Transfer request submitted successfully');
        setShowTransferDialog(false);
        setTransferForm({ source_project_id: '', destination_project_id: '', reason: '', materials_description: '' });
        fetchTransfers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit transfer request');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  return (
    <div className="employee-dashboard modern-bg" data-testid="project-teams-dashboard">
      {/* Company Branding Header - Same as FieldDashboard */}
      <div className="company-header">
        <div className="company-branding">
          <div className="company-logo-circle">
            {companySettings.logo_url ? (
              <img src={companySettings.logo_url} alt="Company logo" className="company-logo-img" />
            ) : (
              <span className="company-initials">
                {companySettings.name ? companySettings.name.substring(0, 2).toUpperCase() : 'CO'}
              </span>
            )}
          </div>
          <div className="company-info">
            <h2 
              className="company-name"
              style={{ 
                color: companySettings.name_color || '#1e293b',
                fontFamily: companySettings.name_font || 'Inter'
              }}
            >
              {companySettings.name || 'Company Name'}
            </h2>
            <p className="company-tagline">
              {companySettings.tagline || 'Project Teams Portal'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <PushNotificationManager authToken={localStorage.getItem('token')} compact user={user} />
          <Button onClick={() => setShowMyProfileDialog(true)} variant="outline" size="sm" className="text-xs">
            <User className="w-3 h-3 mr-1" />
            <span className="hidden sm:inline">My Profile</span>
            <span className="sm:hidden">Profile</span>
            {isFirstTimeUser && <span className="ml-1 text-red-500">!</span>}
          </Button>
          <Button onClick={() => setShowChangePasswordDialog(true)} variant="outline" size="sm" className="text-xs">
            <span className="hidden sm:inline">🔒 Change Password</span>
            <span className="sm:hidden">🔒</span>
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setShowHelpCenter(true)}
            className="text-gray-600 hover:text-blue-600 h-8 w-8"
            data-testid="help-btn"
          >
            <HelpCircle className="w-5 h-5" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={async () => {
              toast.info('Updating app...');
              await forceAppUpdate();
            }}
            className="text-gray-600 hover:text-green-600 h-8 w-8"
            title="Update App"
            data-testid="update-app-btn"
          >
            <RefreshCw className="w-5 h-5" />
          </Button>
          <Button onClick={handleLogout} variant="outline" size="sm" className="logout-btn text-xs" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* PWA Install Banner */}
      <PWAInstallBanner user={user} />

      {/* iOS Install Instructions Banner */}
      <IOSInstallBanner />

      {/* Personalized Dashboard Header - Matching other dashboards */}
      <header className="dashboard-header-modern">
        <div>
          <h1 className="personalized-title" data-testid="dashboard-title">
            {user.name}'s Dashboard
          </h1>
          <p className="welcome-text">Welcome back! Here's what's happening today.</p>
        </div>
      </header>

      {/* Main Layout: Dashboard + Bulletin Board Side by Side */}
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
        {/* Left Side: Main Dashboard Content */}
        <div className="flex-1 min-w-0">
          {/* Quick Stats - 3 columns */}
          <div className="grid grid-cols-3 gap-2 md:gap-4 mb-4 md:mb-6">
            {/* Hours Today */}
            <Card className="stat-card stat-card-green" data-testid="hours-today-card">
              <CardContent className="pt-3 md:pt-6 pb-3 md:pb-4">
                <div className="text-center">
                  <Clock className="w-5 h-5 md:w-8 md:h-8 mx-auto mb-1 text-green-800" />
                  <p className="text-xs md:text-sm font-semibold text-gray-700">Hours Today</p>
                  <p className="text-xl md:text-3xl font-bold text-green-900">{hoursToday}</p>
                </div>
              </CardContent>
            </Card>

            {/* Clock In / Clock Out */}
            <Card className={`stat-card ${isClockedIn ? 'bg-blue-600' : 'bg-gray-900'}`} data-testid="clock-action-card">
              <CardContent className="pt-3 md:pt-6 pb-3 md:pb-4">
                <div className="text-center">
                  <Clock className={`w-5 h-5 md:w-8 md:h-8 mx-auto mb-1 ${isClockedIn ? 'text-blue-200' : 'text-gray-400'}`} />
                  {isClockedIn ? (
                    <>
                      <p className="text-xs md:text-sm font-semibold text-blue-100 mb-0.5">Clocked In</p>
                      <p className="text-lg md:text-xl font-mono font-bold text-white mb-1" data-testid="running-time">
                        {elapsedTime}
                      </p>
                      <Button 
                        onClick={handleClockOut} 
                        variant="destructive" 
                        size="sm"
                        className="clock-btn"
                        data-testid="clock-out-btn"
                      >
                        Clock Out
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="text-xs md:text-sm font-semibold text-gray-300 mb-1">Ready?</p>
                      <Button 
                        onClick={handleClockIn} 
                        variant="default" 
                        size="sm"
                        className="clock-btn bg-green-600 hover:bg-green-700"
                        data-testid="clock-in-btn"
                      >
                        Clock In
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Location */}
            <Card 
              className="stat-card stat-card-purple cursor-pointer hover:opacity-90 transition-opacity" 
              data-testid="location-card"
              onClick={getCurrentLocation}
            >
              <CardContent className="pt-3 md:pt-6 pb-3 md:pb-4">
                <div className="text-center">
                  <MapPin className="w-5 h-5 md:w-8 md:h-8 mx-auto mb-1 text-purple-300" />
                  <p className="text-xs md:text-sm font-semibold text-purple-200">Your Location</p>
                  {gettingLocation ? (
                    <p className="text-xs text-purple-300 mt-1">Getting location...</p>
                  ) : currentLocation ? (
                    <p className="text-xs text-purple-300 mt-1 truncate max-w-[120px] mx-auto" title={currentLocation.address}>
                      {currentLocation.address}
                    </p>
                  ) : (
                    <p className="text-xs text-purple-300 mt-1">Click to get location</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Personal Stats Section - Green Theme */}
          <div className="personal-stats-section mb-4 md:mb-6" style={{ 
            background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', 
            borderRadius: '1rem', 
            padding: '1rem',
            border: '2px solid #10b981'
          }}>
            <h3 style={{ 
              fontSize: '0.875rem', 
              fontWeight: '600', 
              color: '#065f46', 
              marginBottom: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              👤 Your Personal Stats
            </h3>
            <div className="grid grid-cols-3 gap-2 md:gap-3">
              {/* Your Weekly Hours */}
              <Card 
                className="stat-card clickable cursor-pointer hover:shadow-lg transition-shadow" 
                data-testid="your-weekly-hours-card"
                onClick={() => handlePersonalStatClick('weekly-hours')}
                style={{ background: 'white', border: '1px solid #a7f3d0' }}
              >
                <CardContent className="pt-3 pb-3">
                  <div className="text-center">
                    <p className="text-xs font-semibold text-green-700">Your Weekly Hours</p>
                    <p className="text-xl md:text-2xl font-bold text-green-600">{personalStats.userWeeklyHours}</p>
                    <p className="text-xs text-green-600">Hours You Worked</p>
                  </div>
                </CardContent>
              </Card>

              {/* Your Bi-Weekly Hours */}
              <Card 
                className="stat-card clickable cursor-pointer hover:shadow-lg transition-shadow" 
                data-testid="your-bi-weekly-hours-card"
                onClick={() => handlePersonalStatClick('biweekly-hours')}
                style={{ background: 'white', border: '1px solid #a7f3d0' }}
              >
                <CardContent className="pt-3 pb-3">
                  <div className="text-center">
                    <p className="text-xs font-semibold text-green-700">Your Bi-Weekly Hours</p>
                    <p className="text-xl md:text-2xl font-bold text-green-600">{personalStats.userBiWeeklyHours}</p>
                    <p className="text-xs text-green-600">
                      {personalStats.biWeeklyPeriodStart && personalStats.biWeeklyPeriodEnd ? (
                        <>{formatDateDisplay(personalStats.biWeeklyPeriodStart)} - {formatDateDisplay(personalStats.biWeeklyPeriodEnd)}</>
                      ) : 'This Pay Period'}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Your Bi-Weekly Pay */}
              <Card 
                className="stat-card clickable cursor-pointer hover:shadow-lg transition-shadow" 
                data-testid="your-bi-weekly-pay-card"
                onClick={() => handlePersonalStatClick('biweekly-pay')}
                style={{ background: 'white', border: '1px solid #a7f3d0' }}
              >
                <CardContent className="pt-3 pb-3">
                  <div className="text-center">
                    <p className="text-xs font-semibold text-green-700">Your Bi-Weekly Pay</p>
                    <p className="text-xl md:text-2xl font-bold text-green-600">${(personalStats.userBiWeeklyPay || 0).toFixed(2)}</p>
                    <p className="text-xs text-green-600">Your Earnings</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Action Buttons Row - Styled like other dashboards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex lg:flex-wrap gap-2 mb-4 md:mb-6">
            <Button 
              onClick={() => setShowMaterialOrderDialog(true)}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-md text-xs sm:text-sm"
              size="default"
            >
              <Package className="w-4 h-4 mr-1 sm:mr-2" />
              Material Order
            </Button>
            <Button 
              onClick={() => setShowOrdersDialog(true)}
              className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white shadow-md text-xs sm:text-sm"
              size="default"
            >
              <Package className="w-4 h-4 mr-1 sm:mr-2" />
              Open Orders
            </Button>
            <Button 
              onClick={() => setShowProjectsDialog(true)}
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-md text-xs sm:text-sm"
              size="default"
            >
              <Calendar className="w-4 h-4 mr-1 sm:mr-2" />
              My Projects
            </Button>
            <Button 
              onClick={() => setShowReturnDialog(true)}
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-md text-xs sm:text-sm"
              size="default"
            >
              <TrendingUp className="w-4 h-4 mr-1 sm:mr-2" />
              Request Return
            </Button>
            <Button 
              onClick={() => setShowTransferDialog(true)}
              className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white shadow-md text-xs sm:text-sm"
              size="default"
            >
              <TrendingUp className="w-4 h-4 mr-1 sm:mr-2" />
              Transfer
            </Button>
            <Button 
              onClick={() => setShowExpenseDialog(true)}
              className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white shadow-md text-xs sm:text-sm"
              size="default"
            >
              <FileText className="w-4 h-4 mr-1 sm:mr-2" />
              Expense
            </Button>
          </div>

          {/* Main Content Grid - Cards with colored headers */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
            {/* Time Tracking Card */}
            <Card className="border-t-4 border-t-blue-500" data-testid="time-tracking-card">
              <CardHeader className="bg-blue-50">
                <CardTitle className="flex items-center gap-2 text-blue-700">
                  <Clock className="w-5 h-5" />
                  Time Tracking
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Status:</span>
                    <Badge variant={isClockedIn ? 'default' : 'secondary'}>
                      {isClockedIn ? 'Clocked In' : 'Clocked Out'}
                    </Badge>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm font-medium mb-2">Recent Entries</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {timeEntries.slice(0, 5).map((entry, idx) => (
                        <div key={idx} className="text-xs text-gray-600 p-1 bg-gray-50 rounded">
                          <p><strong>Clock In:</strong> {entry.clock_in ? new Date(entry.clock_in).toLocaleString() : 'N/A'}</p>
                          {entry.clock_out && (
                            <p><strong>Clock Out:</strong> {new Date(entry.clock_out).toLocaleString()}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* My Schedule Card */}
            <Card className="border-t-4 border-t-green-500" data-testid="schedule-card">
              <CardHeader className="bg-green-50">
                <CardTitle className="flex items-center gap-2 text-green-700">
                  <Calendar className="w-5 h-5" />
                  My Schedule
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {schedules.length > 0 ? (
                    schedules.slice(0, 5).map((schedule, idx) => (
                      <div key={idx} className="p-2 bg-gray-50 rounded text-sm">
                        <p className="font-medium">{schedule.project_name}</p>
                        <p className="text-xs text-gray-600">{formatDateDisplay(schedule.date)}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No upcoming schedules</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* My Tasks Card */}
            <MyTasksCard tasks={tasks} onRefresh={fetchData} />

            {/* Team Messages Card */}
            <Card className="border-t-4 border-t-purple-500" data-testid="messages-card">
              <CardHeader className="bg-purple-50">
                <CardTitle className="flex items-center gap-2 text-purple-700">
                  <MessageSquare className="w-5 h-5" />
                  Team Messages
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-24 overflow-y-auto mb-2">
                  {messages.slice(0, 3).map((msg, idx) => (
                    <div key={idx} className="text-xs p-1 bg-gray-50 rounded">
                      <strong>{msg.sender_name}:</strong> {msg.content?.substring(0, 50)}...
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input 
                    placeholder="Type a message..." 
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    className="flex-1"
                  />
                  <Button onClick={handleSendMessage} size="sm">Send</Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ===== PROJECT TIME TRACKING SECTION (Office Staff Feature) ===== */}
          <Card className="mb-6 border-2 border-green-200" data-testid="project-time-tracking-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-green-800">
                <Clock className="w-5 h-5" />
                Project Time Tracking
                {activeProjectTime && (
                  <Badge variant="default" className="ml-2 bg-green-600 animate-pulse">
                    Tracking Active
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Start/Stop Project Time */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Select Project</Label>
                  <Select 
                    value={selectedProjectForTime} 
                    onValueChange={setSelectedProjectForTime}
                    disabled={!!activeProjectTime}
                  >
                    <SelectTrigger data-testid="project-time-select">
                      <SelectValue placeholder="Choose a project..." />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Pre-Construction Projects */}
                      {preConstructionProjects.length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50">
                            📋 Pre-Construction
                          </div>
                          {preConstructionProjects.map(project => (
                            <SelectItem key={project.id} value={project.id}>
                              {project.name}
                            </SelectItem>
                          ))}
                        </>
                      )}
                      
                      {/* Construction Projects */}
                      {constructionProjects.length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-orange-600 bg-orange-50 mt-1">
                            🏗️ Construction
                          </div>
                          {constructionProjects.map(project => (
                            <SelectItem key={project.id} value={project.id}>
                              {project.name}
                            </SelectItem>
                          ))}
                        </>
                      )}
                      
                      {/* Fallback for projects without stage */}
                      {allProjects.filter(p => !p.stage || (p.stage !== 'pre_construction' && p.stage !== 'construction')).length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-gray-600 bg-gray-50 mt-1">
                            📁 Other Projects
                          </div>
                          {allProjects.filter(p => !p.stage || (p.stage !== 'pre_construction' && p.stage !== 'construction')).map(project => (
                            <SelectItem key={project.id} value={project.id}>
                              {project.name}
                            </SelectItem>
                          ))}
                        </>
                      )}
                      
                      {/* No projects message */}
                      {allProjects.length === 0 && (
                        <div className="px-2 py-3 text-sm text-gray-500 text-center">
                          No projects available
                        </div>
                      )}
                    </SelectContent>
                  </Select>

                  {activeProjectTime ? (
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-sm text-green-600 mb-1">Currently tracking:</p>
                      <p className="font-semibold text-green-800">{activeProjectTime.project_name}</p>
                      <p className="text-xs text-green-600 mt-1">
                        Started: {new Date(activeProjectTime.start_time).toLocaleTimeString()}
                      </p>
                      <Button 
                        onClick={handleStopProjectTime}
                        variant="destructive"
                        className="mt-3 w-full"
                        data-testid="stop-project-time-btn"
                      >
                        <Square className="w-4 h-4 mr-2" />
                        Stop Tracking
                      </Button>
                    </div>
                  ) : (
                    <Button 
                      onClick={handleStartProjectTime}
                      className="w-full bg-green-600 hover:bg-green-700"
                      disabled={!selectedProjectForTime}
                      data-testid="start-project-time-btn"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Start Project Time
                    </Button>
                  )}
                </div>

                {/* Project Time Summary */}
                <div>
                  <h4 className="font-medium mb-3 text-sm">Time by Project (This Week)</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {projectTimeSummary.length > 0 ? (
                      projectTimeSummary.map((summary, idx) => (
                        <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded border">
                          <span className="text-sm truncate">{summary.project_name}</span>
                          <Badge variant="secondary">{summary.total_hours} hrs</Badge>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-4">No project time logged yet</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ===== PROJECTS BY STAGE (Office Staff Feature) ===== */}
          <Card className="mb-6" data-testid="projects-by-stage-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Projects by Stage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={activeProjectTab} onValueChange={setActiveProjectTab}>
                <TabsList className="mb-4">
                  <TabsTrigger value="bidding" className="flex items-center gap-2">
                    <Gavel className="w-4 h-4" />
                    Bidding ({biddingProjects.length})
                  </TabsTrigger>
                  <TabsTrigger value="pre_construction" className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Pre-Construction ({preConstructionProjects.length})
                  </TabsTrigger>
                  <TabsTrigger value="construction" className="flex items-center gap-2">
                    <HardHat className="w-4 h-4" />
                    Construction ({constructionProjects.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="bidding">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {biddingProjects.length > 0 ? (
                      biddingProjects.map(project => (
                        <Card key={project.id} className="border-purple-200 bg-purple-50/30">
                          <CardContent className="p-4">
                            <h4 className="font-semibold text-sm">{project.name}</h4>
                            <p className="text-xs text-gray-600 mt-1">{project.address || 'No address'}</p>
                            <Badge variant="outline" className="mt-2 text-xs bg-purple-100 text-purple-700">Bidding</Badge>
                          </CardContent>
                        </Card>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 col-span-3 text-center py-4">No projects in bidding stage</p>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="pre_construction">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {preConstructionProjects.length > 0 ? (
                      preConstructionProjects.map(project => (
                        <Card key={project.id} className="border-blue-200 bg-blue-50/30">
                          <CardContent className="p-4">
                            <h4 className="font-semibold text-sm">{project.name}</h4>
                            <p className="text-xs text-gray-600 mt-1">{project.address || 'No address'}</p>
                            <Badge variant="outline" className="mt-2 text-xs">Pre-Construction</Badge>
                          </CardContent>
                        </Card>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 col-span-3 text-center py-4">No pre-construction projects</p>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="construction">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {constructionProjects.length > 0 ? (
                      constructionProjects.map(project => (
                        <Card key={project.id} className="border-orange-200 bg-orange-50/30">
                          <CardContent className="p-4">
                            <h4 className="font-semibold text-sm">{project.name}</h4>
                            <p className="text-xs text-gray-600 mt-1">{project.address || 'No address'}</p>
                            <Badge variant="outline" className="mt-2 text-xs">Construction</Badge>
                          </CardContent>
                        </Card>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 col-span-3 text-center py-4">No construction projects</p>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Right Side: Bulletin Board */}
        <div className="w-80 flex-shrink-0" data-testid="bulletin-board-section">
          <Card className="sticky top-4 border-2 border-amber-200 bg-amber-50/50">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <FileText className="w-5 h-5" />
                Bulletin Board
              </CardTitle>
              <Button 
                onClick={() => {
                  setEditingNote(null);
                  setNoteForm({ title: '', content: '', color: 'yellow', is_shared: false, pinned: false, priority: 0 });
                  setShowNoteDialog(true);
                }}
                size="sm"
                data-testid="add-note-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-gray-500 mb-3">Moveable cards to set priority</p>
              {bulletinNotes.length > 0 ? (
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {bulletinNotes.map((note, index) => {
                    const colors = getColorClasses(note.color);
                    return (
                      <div 
                        key={note.id}
                        className={`p-3 rounded-lg border-2 ${colors.bg} ${colors.border} relative group`}
                        data-testid={`bulletin-note-${note.id}`}
                      >
                        {/* Move buttons */}
                        {note.user_id === user.id && (
                          <div className="absolute -left-2 top-1/2 -translate-y-1/2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button 
                              onClick={() => moveNoteUp(index)}
                              className="w-5 h-5 bg-white rounded shadow text-xs hover:bg-gray-100"
                              disabled={index === 0}
                            >↑</button>
                            <button 
                              onClick={() => moveNoteDown(index)}
                              className="w-5 h-5 bg-white rounded shadow text-xs hover:bg-gray-100"
                              disabled={index === bulletinNotes.length - 1}
                            >↓</button>
                          </div>
                        )}
                        
                        <div className="flex items-start justify-between mb-2">
                          <h4 className={`font-semibold text-sm ${colors.text}`}>{note.title}</h4>
                          <div className="flex gap-1">
                            {note.pinned && <span className="text-xs">📌</span>}
                            {note.is_shared && <span className="text-xs">👥</span>}
                          </div>
                        </div>
                        <p className="text-xs text-gray-700 whitespace-pre-wrap line-clamp-4">{note.content}</p>
                        <div className="mt-2 pt-2 border-t border-gray-200/50 flex items-center justify-between">
                          <span className="text-xs text-gray-500">
                            {note.user_id === user.id ? 'You' : (note.user_name || 'Unknown')}
                          </span>
                          {note.user_id === user.id && (
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                className="h-6 w-6"
                                onClick={() => {
                                  setEditingNote(note);
                                  setNoteForm({
                                    title: note.title,
                                    content: note.content,
                                    color: note.color,
                                    is_shared: note.is_shared,
                                    pinned: note.pinned,
                                    priority: note.priority || 0
                                  });
                                  setShowNoteDialog(true);
                                }}
                                data-testid={`edit-note-${note.id}`}
                              >
                                ✏️
                              </Button>
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                className="h-6 w-6 text-red-500"
                                onClick={() => handleDeleteNote(note.id)}
                                data-testid={`delete-note-${note.id}`}
                              >
                                🗑️
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No notes yet</p>
                  <p className="text-xs">Click "Add" to create one!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ===== DIALOGS ===== */}

      {/* Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent className="max-w-md" data-testid="note-dialog">
          <DialogHeader>
            <DialogTitle>{editingNote ? 'Edit Note' : 'Create Note'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={noteForm.title}
                onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
                placeholder="Note title..."
                data-testid="note-title-input"
              />
            </div>
            <div>
              <Label>Content *</Label>
              <Textarea
                value={noteForm.content}
                onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
                placeholder="Write your note here..."
                rows={4}
                data-testid="note-content-input"
              />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex gap-2 mt-2">
                {NOTE_COLORS.map(color => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setNoteForm({ ...noteForm, color: color.value })}
                    className={`w-8 h-8 rounded-full border-2 ${color.bg} ${
                      noteForm.color === color.value ? 'ring-2 ring-offset-2 ring-blue-500' : ''
                    }`}
                    title={color.name}
                  />
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={noteForm.is_shared}
                  onChange={(e) => setNoteForm({ ...noteForm, is_shared: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Share with team</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={noteForm.pinned}
                  onChange={(e) => setNoteForm({ ...noteForm, pinned: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Pin to top</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowNoteDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveNote} 
                disabled={!noteForm.title.trim() || !noteForm.content.trim()}
                data-testid="save-note-btn"
              >
                {editingNote ? 'Update Note' : 'Create Note'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Material Order Dialog */}
      <Dialog open={showMaterialOrderDialog} onOpenChange={setShowMaterialOrderDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Material Order</DialogTitle>
          </DialogHeader>
          <MaterialOrderForm 
            projects={allProjects} 
            onSuccess={() => {
              setShowMaterialOrderDialog(false);
              fetchData();
            }}
            onCancel={() => setShowMaterialOrderDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* My Profile Dialog */}
      <Dialog open={showMyProfileDialog} onOpenChange={setShowMyProfileDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="my-profile-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <User className="w-6 h-6" />
              My Profile
              {isFirstTimeUser && (
                <Badge variant="warning" className="ml-2">Please Complete</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="personal" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="personal" data-testid="personal-tab">
                Personal Info
              </TabsTrigger>
              <TabsTrigger value="documents" data-testid="documents-tab">
                Documents (Optional)
              </TabsTrigger>
            </TabsList>

            {/* Personal Details Tab */}
            <TabsContent value="personal" className="space-y-4">
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <h4 className="font-semibold text-indigo-900 mb-2">Personal Information</h4>
                <p className="text-sm text-indigo-700">Keep your personal details up to date for payroll and emergency contacts.</p>
              </div>
              <Button 
                onClick={() => setShowPersonalDetailsDialog(true)} 
                className="w-full"
                data-testid="edit-personal-details-btn"
              >
                <User className="w-4 h-4 mr-2" />
                Edit Personal Details
              </Button>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-gray-500">Name</p>
                  <p className="font-medium">{user?.name || '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-gray-500">Email</p>
                  <p className="font-medium">{user?.email || '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-gray-500">Phone</p>
                  <p className="font-medium">{user?.phone || '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-gray-500">Role</p>
                  <p className="font-medium capitalize">{user?.role?.replace('_', ' ') || '-'}</p>
                </div>
              </div>
            </TabsContent>

            {/* Documents Tab - Optional for Office Staff */}
            <TabsContent value="documents" className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">Professional Documents (Optional)</h4>
                <p className="text-sm text-blue-700">
                  As office staff, certificates and credentials are optional. Add any professional certifications if you have them.
                </p>
              </div>

              {/* Certificates Section */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h5 className="font-semibold flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Certificates
                    {myProfile?.certificates?.length > 0 && (
                      <Badge variant="secondary">{myProfile.certificates.length}</Badge>
                    )}
                  </h5>
                  <Button 
                    size="sm" 
                    onClick={() => setShowAddCertificateDialog(true)}
                    data-testid="add-certificate-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>
                {myProfile?.certificates?.length > 0 ? (
                  <div className="space-y-2">
                    {myProfile.certificates.map((cert, idx) => {
                      const isExpired = cert.expiry_date && new Date(cert.expiry_date) < new Date();
                      return (
                        <div 
                          key={cert.id || idx} 
                          className={`p-3 rounded-lg border ${isExpired ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">{cert.name}</span>
                                {isExpired && <Badge variant="destructive" className="text-xs">Expired</Badge>}
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                {cert.certificate_number && <p>#{cert.certificate_number}</p>}
                                {cert.expiry_date && (
                                  <p className={isExpired ? 'text-red-600' : ''}>
                                    Expires: {new Date(cert.expiry_date).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                              {cert.document_url ? (
                                <a 
                                  href={`${API_URL}${cert.document_url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline flex items-center gap-1 text-xs mt-1"
                                >
                                  <FileText className="w-3 h-3" />
                                  View Document
                                </a>
                              ) : (
                                <label className="cursor-pointer text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1">
                                  <Upload className="w-3 h-3" />
                                  Attach Document
                                  <input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) uploadCertificateDocument(cert.id, file);
                                    }}
                                  />
                                </label>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => deleteMyCertificate(cert.id)}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No certificates added yet</p>
                )}
              </div>

              {/* Licenses Section */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h5 className="font-semibold flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Licenses
                    {myProfile?.licenses?.length > 0 && (
                      <Badge variant="secondary">{myProfile.licenses.length}</Badge>
                    )}
                  </h5>
                  <Button 
                    size="sm" 
                    onClick={() => setShowAddLicenseDialog(true)}
                    data-testid="add-license-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>
                {myProfile?.licenses?.length > 0 ? (
                  <div className="space-y-2">
                    {myProfile.licenses.map((license, idx) => {
                      const isExpired = license.expiry_date && new Date(license.expiry_date) < new Date();
                      return (
                        <div 
                          key={license.id || idx} 
                          className={`p-3 rounded-lg border ${isExpired ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">{license.license_type}</span>
                                {isExpired && <Badge variant="destructive" className="text-xs">Expired</Badge>}
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                <p>#{license.license_number}</p>
                                {license.state && <p>State: {license.state}</p>}
                                {license.expiry_date && (
                                  <p className={isExpired ? 'text-red-600' : ''}>
                                    Expires: {new Date(license.expiry_date).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                              {license.document_url ? (
                                <a 
                                  href={`${API_URL}${license.document_url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline flex items-center gap-1 text-xs mt-1"
                                >
                                  <FileText className="w-3 h-3" />
                                  View Document
                                </a>
                              ) : (
                                <label className="cursor-pointer text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1">
                                  <Upload className="w-3 h-3" />
                                  Attach Document
                                  <input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) uploadLicenseDocument(license.id, file);
                                    }}
                                  />
                                </label>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => deleteMyLicense(license.id)}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No licenses added yet</p>
                )}
              </div>

              {/* Credentials Section */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h5 className="font-semibold flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Other Credentials
                    {myProfile?.credentials?.length > 0 && (
                      <Badge variant="secondary">{myProfile.credentials.length}</Badge>
                    )}
                  </h5>
                  <Button 
                    size="sm" 
                    onClick={() => setShowAddCredentialDialog(true)}
                    data-testid="add-credential-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>
                {myProfile?.credentials?.length > 0 ? (
                  <div className="space-y-2">
                    {myProfile.credentials.map((cred, idx) => {
                      const isExpired = cred.expiry_date && new Date(cred.expiry_date) < new Date();
                      return (
                        <div 
                          key={cred.id || idx} 
                          className={`p-3 rounded-lg border ${isExpired ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">{cred.credential_type}</span>
                                {isExpired && <Badge variant="destructive" className="text-xs">Expired</Badge>}
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                {cred.credential_number && <p>#{cred.credential_number}</p>}
                                {cred.notes && <p className="italic">{cred.notes}</p>}
                                {cred.expiry_date && (
                                  <p className={isExpired ? 'text-red-600' : ''}>
                                    Expires: {new Date(cred.expiry_date).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                              {cred.document_url ? (
                                <a 
                                  href={`${API_URL}${cred.document_url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline flex items-center gap-1 text-xs mt-1"
                                >
                                  <FileText className="w-3 h-3" />
                                  View Document
                                </a>
                              ) : (
                                <label className="cursor-pointer text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1">
                                  <Upload className="w-3 h-3" />
                                  Attach Document
                                  <input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) uploadCredentialDocument(cred.id, file);
                                    }}
                                  />
                                </label>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => deleteMyCredential(cred.id)}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No credentials added yet</p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Add Certificate Dialog */}
      <Dialog open={showAddCertificateDialog} onOpenChange={setShowAddCertificateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Certificate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Certificate Name *</Label>
              <Input
                value={newCertificate.name}
                onChange={(e) => setNewCertificate({...newCertificate, name: e.target.value})}
                placeholder="e.g., WHMIS, First Aid, PMP"
              />
            </div>
            <div>
              <Label>Certificate Number</Label>
              <Input
                value={newCertificate.certificate_number}
                onChange={(e) => setNewCertificate({...newCertificate, certificate_number: e.target.value})}
                placeholder="Certificate/License #"
              />
            </div>
            <div>
              <Label>Issuing Authority</Label>
              <Input
                value={newCertificate.issuing_authority}
                onChange={(e) => setNewCertificate({...newCertificate, issuing_authority: e.target.value})}
                placeholder="e.g., Red Cross, OSHA"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Issue Date</Label>
                <Input
                  type="date"
                  value={newCertificate.issue_date}
                  onChange={(e) => setNewCertificate({...newCertificate, issue_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={newCertificate.expiry_date}
                  onChange={(e) => setNewCertificate({...newCertificate, expiry_date: e.target.value})}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddCertificateDialog(false)}>Cancel</Button>
              <Button onClick={addMyCertificate}>Add Certificate</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add License Dialog */}
      <Dialog open={showAddLicenseDialog} onOpenChange={setShowAddLicenseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add License</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>License Type *</Label>
              <Input
                value={newLicense.license_type}
                onChange={(e) => setNewLicense({...newLicense, license_type: e.target.value})}
                placeholder="e.g., Driver's License, Trade License"
              />
            </div>
            <div>
              <Label>License Number *</Label>
              <Input
                value={newLicense.license_number}
                onChange={(e) => setNewLicense({...newLicense, license_number: e.target.value})}
                placeholder="License #"
              />
            </div>
            <div>
              <Label>State/Province</Label>
              <Input
                value={newLicense.state}
                onChange={(e) => setNewLicense({...newLicense, state: e.target.value})}
                placeholder="e.g., Ontario, California"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Issue Date</Label>
                <Input
                  type="date"
                  value={newLicense.issue_date}
                  onChange={(e) => setNewLicense({...newLicense, issue_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={newLicense.expiry_date}
                  onChange={(e) => setNewLicense({...newLicense, expiry_date: e.target.value})}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddLicenseDialog(false)}>Cancel</Button>
              <Button onClick={addMyLicense}>Add License</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Credential Dialog */}
      <Dialog open={showAddCredentialDialog} onOpenChange={setShowAddCredentialDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Credential</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Credential Type *</Label>
              <Input
                value={newCredential.credential_type}
                onChange={(e) => setNewCredential({...newCredential, credential_type: e.target.value})}
                placeholder="e.g., Professional Membership, Union Card"
              />
            </div>
            <div>
              <Label>ID/Number</Label>
              <Input
                value={newCredential.credential_number}
                onChange={(e) => setNewCredential({...newCredential, credential_number: e.target.value})}
                placeholder="Credential ID or Number"
              />
            </div>
            <div>
              <Label>Expiry Date</Label>
              <Input
                type="date"
                value={newCredential.expiry_date}
                onChange={(e) => setNewCredential({...newCredential, expiry_date: e.target.value})}
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={newCredential.notes}
                onChange={(e) => setNewCredential({...newCredential, notes: e.target.value})}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddCredentialDialog(false)}>Cancel</Button>
              <Button onClick={addMyCredential}>Add Credential</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Personal Details Dialog (Full Editor) */}
      <PersonalDetailsDialog 
        open={showPersonalDetailsDialog}
        onOpenChange={setShowPersonalDetailsDialog}
        user={user}
        onUpdate={fetchMyProfile}
      />

      {/* Change Password Dialog */}
      <ChangePasswordDialog 
        isOpen={showChangePasswordDialog} 
        onClose={() => setShowChangePasswordDialog(false)} 
      />

      {/* Help Center Dialog */}
      <Dialog open={showHelpCenter} onOpenChange={setShowHelpCenter}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <HelpCenter onClose={() => setShowHelpCenter(false)} />
        </DialogContent>
      </Dialog>

      {/* View Open Orders Dialog */}
      <Dialog open={showOrdersDialog} onOpenChange={setShowOrdersDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Open Material Orders</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Created Orders */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Badge variant="warning">Created Orders</Badge>
                <span className="text-sm text-gray-500">
                  ({materialOrders.filter(o => o.status === 'pending' || o.status === 'created').length})
                </span>
              </h4>
              <div className="space-y-2">
                {materialOrders
                  .filter(order => order.status === 'pending' || order.status === 'created')
                  .map((order) => {
                    const project = allProjects.find(p => p.id === order.project_id);
                    return (
                      <div 
                        key={order.id} 
                        className="p-3 border rounded hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowOrderDetails(true);
                          setShowOrdersDialog(false);
                        }}
                      >
                        <div className="flex justify-between">
                          <div>
                            <strong>{project?.name || 'Unknown Project'}</strong>
                            <p className="text-sm text-gray-600">
                              {new Date(order.order_date).toLocaleDateString()}
                            </p>
                          </div>
                          <Badge variant="warning">{order.status}</Badge>
                        </div>
                      </div>
                    );
                  })}
                {materialOrders.filter(o => o.status === 'pending' || o.status === 'created').length === 0 && (
                  <p className="text-gray-500 text-sm italic">No created orders</p>
                )}
              </div>
            </div>

            {/* Ordered */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Badge variant="default">Ordered</Badge>
                <span className="text-sm text-gray-500">
                  ({materialOrders.filter(o => o.status === 'ordered').length})
                </span>
              </h4>
              <div className="space-y-2">
                {materialOrders
                  .filter(order => order.status === 'ordered')
                  .map((order) => {
                    const project = allProjects.find(p => p.id === order.project_id);
                    return (
                      <div 
                        key={order.id} 
                        className="p-3 border rounded hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowOrderDetails(true);
                          setShowOrdersDialog(false);
                        }}
                      >
                        <div className="flex justify-between">
                          <div>
                            <strong>{project?.name || 'Unknown Project'}</strong>
                            <p className="text-sm text-gray-600">
                              {new Date(order.order_date).toLocaleDateString()}
                            </p>
                          </div>
                          <Badge variant="default">{order.status}</Badge>
                        </div>
                      </div>
                    );
                  })}
                {materialOrders.filter(o => o.status === 'ordered').length === 0 && (
                  <p className="text-gray-500 text-sm italic">No ordered items</p>
                )}
              </div>
            </div>

            {/* Received at Site */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Badge className="bg-green-600">Received at Site</Badge>
                <span className="text-sm text-gray-500">
                  ({materialOrders.filter(o => o.status === 'received_at_site').length})
                </span>
              </h4>
              <div className="space-y-2">
                {materialOrders
                  .filter(order => order.status === 'received_at_site')
                  .map((order) => {
                    const project = allProjects.find(p => p.id === order.project_id);
                    return (
                      <div 
                        key={order.id} 
                        className="p-3 border rounded hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowOrderDetails(true);
                          setShowOrdersDialog(false);
                        }}
                      >
                        <div className="flex justify-between">
                          <div>
                            <strong>{project?.name || 'Unknown Project'}</strong>
                            <p className="text-sm text-gray-600">
                              {new Date(order.order_date).toLocaleDateString()}
                            </p>
                          </div>
                          <Badge className="bg-green-600">{order.status}</Badge>
                        </div>
                      </div>
                    );
                  })}
                {materialOrders.filter(o => o.status === 'received_at_site').length === 0 && (
                  <p className="text-gray-500 text-sm italic">No received items</p>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order Details Dialog */}
      <Dialog open={showOrderDetails} onOpenChange={setShowOrderDetails}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">Project</p>
                <p className="font-semibold">
                  {allProjects.find(p => p.id === selectedOrder.project_id)?.name || 'Unknown'}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Order Date</p>
                  <p className="font-semibold">
                    {new Date(selectedOrder.order_date).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Delivery Date</p>
                  <p className="font-semibold">
                    {selectedOrder.delivery_date ? new Date(selectedOrder.delivery_date).toLocaleDateString() : 'Not set'}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <Badge variant={
                  selectedOrder.status === 'received_at_site' ? 'success' :
                  selectedOrder.status === 'ordered' ? 'default' : 'warning'
                }>
                  {selectedOrder.status}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-600">Order Type</p>
                <p className="font-semibold">{selectedOrder.order_type}</p>
              </div>
              {selectedOrder.notes && (
                <div>
                  <p className="text-sm text-gray-600">Notes</p>
                  <p>{selectedOrder.notes}</p>
                </div>
              )}
              <div className="pt-4 border-t">
                <Button onClick={() => setShowOrderDetails(false)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* My Projects Dialog */}
      <Dialog open={showProjectsDialog} onOpenChange={setShowProjectsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>My Projects</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {allProjects.map(project => (
                <Card 
                  key={project.id} 
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    selectedProject?.id === project.id ? 'border-blue-500 border-2' : ''
                  }`}
                  onClick={() => setSelectedProject(project)}
                >
                  <CardContent className="p-4">
                    <h4 className="font-semibold">{project.name}</h4>
                    <p className="text-sm text-gray-600">{project.address || 'No address'}</p>
                    <Badge variant={project.stage === 'pre_construction' ? 'outline' : 'default'} className="mt-2">
                      {project.stage === 'pre_construction' ? 'Pre-Construction' : 'Construction'}
                    </Badge>
                  </CardContent>
                </Card>
              ))}
              {allProjects.length === 0 && (
                <p className="text-gray-500 italic col-span-3">No projects available</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Return Request Dialog */}
      <Dialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Request Material Return</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Project *</Label>
              <ProjectCombobox
                projects={allProjects}
                value={returnForm.project_id}
                onChange={(value) => setReturnForm({ ...returnForm, project_id: value })}
                placeholder="Select project"
                filterActive={false}
                testId="return-project"
              />
            </div>

            <div>
              <Label>Reason for Return *</Label>
              <Select
                value={returnForm.reason}
                onValueChange={(value) => setReturnForm({ ...returnForm, reason: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="wrong_material_delivered">Wrong Material Delivered</SelectItem>
                  <SelectItem value="wrong_material_ordered">Wrong Material Ordered</SelectItem>
                  <SelectItem value="remaining_after_completion">Remaining After Project Completion</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Materials Description *</Label>
              <Textarea
                value={returnForm.materials_description}
                onChange={(e) => setReturnForm({ ...returnForm, materials_description: e.target.value })}
                placeholder="Describe the materials to return..."
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowReturnDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitReturn}
                disabled={!returnForm.project_id || !returnForm.reason || !returnForm.materials_description}
              >
                Submit Return Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Request Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Request Material Transfer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>From Project *</Label>
                <ProjectCombobox
                  projects={allProjects}
                  value={transferForm.source_project_id}
                  onChange={(value) => setTransferForm({ ...transferForm, source_project_id: value })}
                  placeholder="Select source project"
                  filterActive={false}
                  testId="transfer-source-project"
                />
              </div>

              <div>
                <Label>To Project *</Label>
                <ProjectCombobox
                  projects={allProjects.filter(p => p.id !== transferForm.source_project_id)}
                  value={transferForm.destination_project_id}
                  onChange={(value) => setTransferForm({ ...transferForm, destination_project_id: value })}
                  placeholder="Select destination project"
                  filterActive={false}
                  testId="transfer-dest-project"
                />
              </div>
            </div>

            <div>
              <Label>Reason for Transfer</Label>
              <Textarea
                value={transferForm.reason}
                onChange={(e) => setTransferForm({ ...transferForm, reason: e.target.value })}
                placeholder="Why are you transferring these materials?"
                rows={2}
              />
            </div>

            <div>
              <Label>Materials Description *</Label>
              <Textarea
                value={transferForm.materials_description}
                onChange={(e) => setTransferForm({ ...transferForm, materials_description: e.target.value })}
                placeholder="Describe the materials to transfer..."
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowTransferDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitTransfer}
                disabled={!transferForm.source_project_id || !transferForm.destination_project_id || !transferForm.materials_description}
              >
                Submit Transfer Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Expense Dialog */}
      <Dialog open={showExpenseDialog} onOpenChange={setShowExpenseDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Submit Expense for Reimbursement</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitExpense} className="space-y-4">
            <div>
              <Label>Expense Type *</Label>
              <Select 
                value={expenseForm.expense_type} 
                onValueChange={(val) => setExpenseForm({ ...expenseForm, expense_type: val })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="parking">Parking</SelectItem>
                  <SelectItem value="materials">Materials</SelectItem>
                  <SelectItem value="tools">Tools</SelectItem>
                  <SelectItem value="fuel">Fuel</SelectItem>
                  <SelectItem value="meals">Meals</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Amount ($) *</Label>
              <Input
                type="number"
                step="0.01"
                value={expenseForm.amount}
                onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                placeholder="0.00"
                required
              />
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                placeholder="Describe the expense..."
                rows={3}
                required
              />
            </div>

            <div>
              <Label>Upload Receipts/Invoices</Label>
              <Input
                type="file"
                multiple
                accept="image/*,.pdf"
                onChange={(e) => setExpenseReceipts(Array.from(e.target.files))}
                className="cursor-pointer"
              />
              <p className="text-xs text-gray-500 mt-1">
                Upload photos of receipts or invoice PDFs (optional but recommended)
              </p>
              {expenseReceipts.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-medium">Selected files:</p>
                  <ul className="text-sm text-gray-600">
                    {expenseReceipts.map((file, i) => (
                      <li key={i}>• {file.name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="p-3 bg-blue-50 rounded border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> This expense will be automatically linked to the current bi-weekly pay period.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowExpenseDialog(false);
                  setExpenseForm({ expense_type: 'parking', amount: '', description: '', pay_period_start: '', pay_period_end: '' });
                  setExpenseReceipts([]);
                }}
                disabled={uploadingReceipts}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={uploadingReceipts}>
                {uploadingReceipts ? 'Submitting...' : 'Submit Expense'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Personal Stats Breakdown Dialog */}
      <Dialog open={showPersonalStatsDialog} onOpenChange={setShowPersonalStatsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {personalStatsType === 'weekly-hours' && 'Your Weekly Hours Breakdown'}
              {personalStatsType === 'biweekly-hours' && 'Your Bi-Weekly Hours Breakdown'}
              {personalStatsType === 'biweekly-pay' && 'Your Bi-Weekly Pay Breakdown'}
            </DialogTitle>
          </DialogHeader>
          <div className="personal-breakdown-content">
            {personalStatsLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
                <p className="text-gray-600">Fetching your {personalStatsType === 'biweekly-pay' ? 'pay' : 'hours'}...</p>
              </div>
            ) : (
              <div className="personal-hours-breakdown">
                {/* Payroll-sacred: surface any time records that need human review */}
                <UnresolvedEntriesAlert
                  unresolvedEntries={personalStatsData?.unresolved_entries || []}
                  unresolvedAdjustments={personalStatsData?.unresolved_adjustments || []}
                  isAdmin={false}
                />
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-green-800">
                      👤 {personalStatsData?.employee_name || 'Your'} {personalStatsType === 'biweekly-pay' ? 'Earnings' : 'Hours'}
                    </h3>
                    <div className="text-2xl font-bold text-green-600">
                      {personalStatsType === 'biweekly-pay' 
                        ? `$${(personalStats.userBiWeeklyPay || 0).toFixed(2)} total`
                        : `${personalStatsData?.total_hours || 0} hrs total`
                      }
                    </div>
                  </div>
                  {personalStatsType === 'biweekly-pay' && (
                    <p className="text-sm text-green-600 mt-2">
                      Based on {personalStatsData?.total_hours || 0} hours worked
                    </p>
                  )}
                </div>
                
                <div className="space-y-3">
                  {personalStatsData?.days?.map((day, idx) => {
                    // Calculate hourly rate from total pay / total hours
                    const hourlyRate = personalStats.userBiWeeklyHours > 0 
                      ? personalStats.userBiWeeklyPay / personalStats.userBiWeeklyHours 
                      : 0;
                    const dayPay = day.hours * hourlyRate;
                    
                    return (
                    <Card key={idx} className={`${day.is_today ? 'border-2 border-green-500 bg-green-50' : ''}`}>
                      <CardContent className="pt-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-semibold flex items-center gap-2">
                              {day.day}
                              {day.is_today && <Badge variant="success" className="text-xs">Today</Badge>}
                            </p>
                            <p className="text-sm text-gray-500">{formatDateDisplay(day.date)}</p>
                          </div>
                          <div className="text-right">
                            {personalStatsType === 'biweekly-pay' ? (
                              <>
                                <p className={`text-xl font-bold ${dayPay > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                                  ${dayPay.toFixed(2)}
                                </p>
                                <p className="text-xs text-gray-500">{day.hours} hrs @ ${hourlyRate.toFixed(2)}/hr</p>
                              </>
                            ) : (
                              <>
                                <p className={`text-xl font-bold ${day.hours > 0 ? 'text-green-600' : day.hours < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                  {day.hours} hrs
                                </p>
                                {day.adjustment_total && day.adjustment_total !== 0 && (
                                  <p className={`text-xs ${day.adjustment_total > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                                    ({day.adjustment_total > 0 ? '+' : ''}{day.adjustment_total} adjusted)
                                  </p>
                                )}
                                {day.entries && day.entries.length > 0 && (
                                  <p className="text-xs text-gray-500">{day.entries.length} shift(s)</p>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        {personalStatsType !== 'biweekly-pay' && day.entries && day.entries.length > 0 && (
                          <div className="mt-3 pt-3 border-t space-y-2">
                            {day.entries.map((entry, entryIdx) => (
                              <div key={entryIdx} className="flex justify-between text-sm bg-gray-50 p-2 rounded">
                                <span className="text-gray-600">
                                  {new Date(entry.clock_in).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - 
                                  {new Date(entry.clock_out).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </span>
                                <span className="font-medium">{entry.hours} hrs</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Show adjustment details */}
                        {day.adjustments && day.adjustments.length > 0 && (
                          <div className="mt-2 pt-2 border-t">
                            {day.adjustments.map((adj, adjIdx) => (
                              <div key={adjIdx} className={`text-xs p-2 rounded ${adj.adjustment_hours > 0 ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700'}`}>
                                <span className="font-medium">{adj.adjustment_hours > 0 ? '+' : ''}{adj.adjustment_hours} hrs</span>
                                <span className="mx-1">•</span>
                                <span>{adj.reason}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    );
                  })}
                  {(!personalStatsData?.days || personalStatsData.days.length === 0) && (
                    <p className="text-center text-gray-500 py-8">No hours logged this week</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Location Change Detection Dialog */}
      <Dialog open={showLocationChangeDialog} onOpenChange={setShowLocationChangeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-orange-500" />
              Location Change Detected
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-orange-800">
                You've moved <strong>{locationChangeData?.distance_moved || 0}m</strong> from your clock-in location.
              </p>
              <p className="text-sm text-orange-600 mt-2">
                Would you like to clock out, or mark this time differently?
              </p>
            </div>
            
            <div className="space-y-3">
              <Button 
                className="w-full" 
                variant="destructive"
                onClick={() => {
                  setShowLocationChangeDialog(false);
                  // Trigger clock out flow
                  handleClockOut();
                }}
                data-testid="location-clock-out-btn"
              >
                <Clock className="w-4 h-4 mr-2" />
                Clock Out Now
              </Button>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">Or mark as</span>
                </div>
              </div>
              
              <Button 
                className="w-full" 
                variant="outline"
                onClick={() => handleDismissLocationChange('site_visit')}
                disabled={updatingWorkType}
                data-testid="mark-site-visit-btn"
              >
                <Building className="w-4 h-4 mr-2" />
                {updatingWorkType ? 'Updating...' : 'Site Visit'}
              </Button>
              
              <Button 
                className="w-full" 
                variant="outline"
                onClick={() => handleDismissLocationChange('work_from_home')}
                disabled={updatingWorkType}
                data-testid="mark-wfh-btn"
              >
                <Home className="w-4 h-4 mr-2" />
                {updatingWorkType ? 'Updating...' : 'Work from Home'}
              </Button>
            </div>
            
            <p className="text-xs text-gray-500 text-center">
              This will be recorded in your time entry for reporting purposes.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Onboarding Tour */}
      <OnboardingTour user={user} />

      {/* Dashboard Styles */}
      <style jsx>{`
        /* Dashboard Header Modern */
        .dashboard-header-modern {
          background: white;
          margin: 1.5rem 2rem;
          padding: 1.5rem 2rem;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }

        .personalized-title {
          font-size: 2.5rem;
          font-weight: 800;
          background: linear-gradient(135deg, #5b63d3 0%, #6b2fa0 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin: 0;
          font-family: 'Poppins', sans-serif;
        }

        .welcome-text {
          font-size: 1.1rem;
          color: #64748b;
          margin: 0.5rem 0 0 0;
          font-weight: 500;
        }

        /* Stat Cards */
        .stat-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-3px);
        }

        .stat-card-green {
          background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
          border: 2px solid rgba(34, 197, 94, 0.3);
        }

        .stat-card-purple {
          background: linear-gradient(135deg, #5b63d3 0%, #6b2fa0 100%);
          border: none;
          color: white;
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .dashboard-header-modern {
            margin: 0.5rem;
            padding: 0.75rem;
          }
          
          .personalized-title {
            font-size: 1.5rem;
          }
          
          .welcome-text {
            font-size: 0.85rem;
          }
          
          .personal-stats-section {
            margin-left: 0.5rem !important;
            margin-right: 0.5rem !important;
            padding: 0.75rem !important;
          }
        }
      `}</style>
    </div>
  );
}
